import { Modal, GlobalAlertService, BannerAlert, Checkbox, Typography } from "@trustpilot/businessapp-patternlibrary";
import { useTranslations, Text, HtmlText, LinkText } from "ilenia";
import { useState } from "react";

import { acceptComplianceNotification } from "../../api";
import { trackingConst } from "../../constants";
import { useAnalytics } from "../../utils/analytics";
import { isTrustpilotEmployee } from "../../utils/employeePermissions";
import { logToSentry } from "../../utils/helpers";
import { makeAllowedMessageType, makeMessageData } from "./data";
import {
  StyleHeaderCopy,
  StyleHeaderCrossHide,
  StyledBannerAlert,
  StyledParagraph,
  StyledCheckboxBanner,
  StyledCheckboxBannerWrapper,
  StyledCheckboxLabel,
  StyledError,
} from "./styled";
import * as Types from "./types";

const ContentIntegrityMessages: React.FC<Types.ContentIntegrityMessages> = ({
  userName,
  userEmail,
  employeePermissions,
  businessUnit,
  messages,
  launchDarkly,
}) => {
  const analytics = useAnalytics();
  const [translations, locale] = useTranslations();
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [checked, setChecked] = useState<boolean>(false);
  const [showError, setShowError] = useState<boolean>(false);
  const [activeMessage, setActiveMessage] = useState<number>(0);

  const {
    id: businessUnitId,
    name: { identifying: domain },
    access: { readOnly },
  } = businessUnit;

  const updateActiveMessage = () => {
    if (activeMessage <= messages.length) {
      setTimeout(() => {
        setChecked(false);
        setIsOpen(true);
      }, 300);
    }
    setActiveMessage(activeMessage + 1);
  };

  if (!messages[activeMessage]) {
    return null;
  }

  const notificationId = messages[activeMessage].notificationId;

  const hasAccessToLegalFabricatedReviewsModal = launchDarkly.includes("b2b-legal-fabricated-reviews");

  const type =
    hasAccessToLegalFabricatedReviewsModal && messages[activeMessage].type === "WarningFabricatedReviews"
      ? `DetectedFabricatedReviews`
      : makeAllowedMessageType(messages[activeMessage].type);
  const details = messages[activeMessage].details;
  const modalData = makeMessageData({ type, domain, details, locale });
  const isTrustee = isTrustpilotEmployee(employeePermissions, userEmail);

  const onClick = () => {
    acceptComplianceNotification(businessUnitId, {
      notificationId,
    }).catch((e) => {
      GlobalAlertService.critical(translations["contentIntegrity.error"], 1500);
      logToSentry(e);
    });

    analytics.track(trackingConst.events.buttonClicked, {
      context: trackingConst.contexts.appshell,
      location: trackingConst.types.contentIntegrity,
      id: trackingConst.names.contentIntegrity[type],
    });

    setIsOpen(false);
    updateActiveMessage();
  };

  const onClose = () => {
    if (readOnly || isTrustee) {
      setIsOpen(false);
    }

    if (!checked) {
      setShowError(true);
    }
  };

  const onChange = () => {
    if (!checked) {
      setShowError(false);
    }
    setChecked(!checked);
  };

  const error = showError && <Text id={`contentIntegrity.${type}.checkboxBanner.error`} />;
  const displayAlert = translations[`contentIntegrity.${type}.bannerAlert`] || false;
  const displayCheckbox = translations[`contentIntegrity.${type}.checkboxBanner.copy`] || false;

  return (
    <Modal
      isOpen={isOpen}
      header={
        // Hack to hide the modal close icon
        <>
          <StyleHeaderCopy>
            <Text id={`contentIntegrity.${type}.header`} />
          </StyleHeaderCopy>
          {!readOnly && !isTrustee && <StyleHeaderCrossHide />}
        </>
      }
      mainActionButton={{
        disabled: readOnly || (displayCheckbox && !checked),
        buttonText: translations[`contentIntegrity.${type}.buttonText`],
        onClick,
      }}
      onClose={onClose}
    >
      <>
        {displayAlert && (
          <StyledBannerAlert>
            <BannerAlert appearance={modalData.bannerAlertAppearance || "warning"}>
              <Text id={`contentIntegrity.${type}.bannerAlert`} />
            </BannerAlert>
          </StyledBannerAlert>
        )}

        {modalData.paragraphs.map(({ id, interpolations }) => (
          <StyledParagraph key={id}>
            <Typography variant="body-l" as="p">
              <HtmlText id={id} interpolations={interpolations} />
            </Typography>
          </StyledParagraph>
        ))}
        {displayCheckbox && (
          <StyledCheckboxBanner error={error}>
            <StyledCheckboxBannerWrapper>
              <Checkbox checked={checked} onChange={onChange}>
                {/* [TODO] Workaround for Checkbox bug in PL - Remove following line when fixed */}
                <div />
              </Checkbox>
              <StyledCheckboxLabel>
                <Typography variant="body-l" as="p">
                  <LinkText
                    id={`contentIntegrity.${type}.checkboxBanner.copy`}
                    links={[
                      {
                        href: "https://legal.trustpilot.com/for-businesses/guidelines-for-businesses",
                        target: "_blank",
                      },
                    ]}
                    interpolations={{
                      USER: userName,
                    }}
                  />
                </Typography>
              </StyledCheckboxLabel>
            </StyledCheckboxBannerWrapper>
            {error && <StyledError id="content-integrity-messages-error">{error}</StyledError>}
          </StyledCheckboxBanner>
        )}
      </>
    </Modal>
  );
};

export default ContentIntegrityMessages;
